import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import { decompile } from '@freelog/nmr_translator';
import { mergeRules } from '@/models/informExhibitInfoPage';
import type {
  InformalNodeManagerPageModelState,
  IActions,
  IRules,
  ChangeAction,
  FetchExhibitListAction,
  FetchThemeListAction,
  FetchReplacerListAction,
  OnClose_ReplaceModal_Action,
  OnReplacerMountAction,
  OnReplacerUnmountAction,
  OnReplacerOriginChangeAction,
  OnReplacerBucketChangeAction,
  OnReplacerKeywordsChangeAction,
  OnReplacerListCheckedChangeAction,
  OnReplacerListVersionRangeChangeAction,
  OnReplacerListLoadMoreAction,
  OnReplacedMountAction,
  OnReplacedUnmountAction,
  OnReplacedKeywordChangeAction,
  OnReplacedEntityVersionChangeAction,
  OnReplacedTreeLoadDataAction,
  OnReplaceModalCancelAction,
  OnReplaceModalConfirmAction,
  RuleMatchAndResultReturn,
} from './types';
import { replaceModalInitDate } from './initialState';
import { ruleMatchAndResult, updateTreeData, organizeData, simplifiedRelationship } from './helpers';

export const replaceModalEffects = {
  * onClose_ReplaceModal({}: OnClose_ReplaceModal_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        ...replaceModalInitDate,
      },
    });
  },
  * onReplacerMount({}: OnReplacerMountAction, { put, call }: EffectsCommandMap) {
    yield put<FetchReplacerListAction>({
      type: 'fetchReplacerList',
      payload: {
        restart: true,
      },
    });

    const params: Parameters<typeof FServiceAPI.Storage.bucketList>[0] = {
      bucketType: 1,
    };

    const { data } = yield call(FServiceAPI.Storage.bucketList, params);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replacer_BucketOptions: (data as any[]).map<InformalNodeManagerPageModelState['replaceModal_Replacer_BucketOptions'][number]>((d: any) => {
          return {
            value: d.bucketName,
            text: d.bucketName,
          };
        }),
        replaceModal_Replacer_Bucket: data.length > 0 ? data[0].bucketName : '',
      },
    });
  },
  * onReplacerUnmount({}: OnReplacerUnmountAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {},
    });
  },
  * onReplacerOriginChange({ payload }: OnReplacerOriginChangeAction, { select, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replacer_Origin: payload.value,
        replaceModal_Replacer_Keywords: '',
        replaceModal_Replacer_Bucket: informalNodeManagerPage.replaceModal_Replacer_BucketOptions.length > 0
          ? informalNodeManagerPage.replaceModal_Replacer_BucketOptions[0].value
          : '',
      },
    });

    yield put<FetchReplacerListAction>({
      type: 'fetchReplacerList',
      payload: {
        restart: true,
      },
    });
  },
  * onReplacerBucketChange({ payload }: OnReplacerBucketChangeAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replacer_Bucket: payload.value,
      },
    });

    yield put<FetchReplacerListAction>({
      type: 'fetchReplacerList',
      payload: {
        restart: true,
      },
    });
  },
  * onReplacerKeywordsChange({ payload }: OnReplacerKeywordsChangeAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replacer_Keywords: payload.value,
      },
    });

    yield put<FetchReplacerListAction>({
      type: 'fetchReplacerList',
      payload: {
        restart: true,
      },
    });
  },
  * fetchReplacerList({ payload }: FetchReplacerListAction, { select, call, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    let replacerResourceList: InformalNodeManagerPageModelState['replaceModal_Replacer_ResourceList'] = [];

    if (!payload.restart) {
      replacerResourceList = [
        ...informalNodeManagerPage.replaceModal_Replacer_ResourceList,
      ];
    }

    if (informalNodeManagerPage.replaceModal_Replacer_Origin === 'market') {

      const params: Parameters<typeof FServiceAPI.Resource.list>[0] = {
        skip: replacerResourceList.length,
        limit: FUtil.Predefined.pageSize,
        keywords: informalNodeManagerPage.replaceModal_Replacer_Keywords,
        status: 1,
      };

      const { data } = yield call(FServiceAPI.Resource.list, params);

      replacerResourceList = [
        ...replacerResourceList,
        ...(data.dataList as any[]).map<InformalNodeManagerPageModelState['replaceModal_Replacer_ResourceList'][number]>((rs) => {
          return {
            checked: false,
            id: rs.resourceId,
            identity: 'resource',
            name: rs.resourceName,
            type: rs.resourceType,
            latestVersion: rs.latestVersion,
            updateTime: FUtil.Format.formatDateTime(rs.updateDate),
            status: rs.status === 1 ? '' : (rs.latestVersion ? 'offline' : 'unreleased'),
            versions: rs.resourceVersions.map((rv: any) => {
              return rv.version;
            }),
            versionRange: '',
          };
        }),
      ];
    } else if (informalNodeManagerPage.replaceModal_Replacer_Origin === 'resource') {

      const params: Parameters<typeof FServiceAPI.Resource.list>[0] = {
        skip: replacerResourceList.length,
        limit: FUtil.Predefined.pageSize,
        isSelf: 1,
        keywords: informalNodeManagerPage.replaceModal_Replacer_Keywords,
      };
      const { data } = yield call(FServiceAPI.Resource.list, params);
      replacerResourceList = [
        ...replacerResourceList,
        ...(data.dataList as any[]).map<InformalNodeManagerPageModelState['replaceModal_Replacer_ResourceList'][number]>((rs) => {
          return {
            checked: false,
            disabled: rs.latestVersion === '',
            id: rs.resourceId,
            identity: 'resource',
            name: rs.resourceName,
            type: rs.resourceType,
            latestVersion: rs.latestVersion,
            updateTime: FUtil.Format.formatDateTime(rs.updateDate),
            status: rs.status === 1 ? '' : (rs.latestVersion ? 'offline' : 'unreleased'),
            versions: rs.resourceVersions.map((rv: any) => {
              return rv.version;
            }),
            versionRange: '',

          };
        }),
      ];
    } else if (informalNodeManagerPage.replaceModal_Replacer_Origin === 'collection') {

      const params: Parameters<typeof FServiceAPI.Collection.collectionResources>[0] = {
        skip: replacerResourceList.length,
        limit: FUtil.Predefined.pageSize,
        keywords: informalNodeManagerPage.replaceModal_Replacer_Keywords,
      };

      const { data } = yield call(FServiceAPI.Collection.collectionResources, params);

      replacerResourceList = [
        ...replacerResourceList,
        ...(data.dataList as any[]).map<InformalNodeManagerPageModelState['replaceModal_Replacer_ResourceList'][number]>((rs) => {
          return {
            checked: false,
            id: rs.resourceId,
            identity: 'resource',
            name: rs.resourceName,
            type: rs.resourceType,
            latestVersion: rs.latestVersion,
            updateTime: FUtil.Format.formatDateTime(rs.updateDate),
            status: rs.status === 1 ? '' : (rs.latestVersion ? 'offline' : 'unreleased'),
            versions: rs.resourceVersions.map((rv: any) => {
              return rv.version;
            }),
            versionRange: '',
          };
        }),
      ];
    } else if (informalNodeManagerPage.replaceModal_Replacer_Origin === 'object') {

      const params: Parameters<typeof FServiceAPI.Storage.objectList>[0] = {
        skip: replacerResourceList.length,
        limit: FUtil.Predefined.pageSize,
        bucketName: informalNodeManagerPage.replaceModal_Replacer_Bucket,
        keywords: informalNodeManagerPage.replaceModal_Replacer_Keywords,
      };

      const { data } = yield call(FServiceAPI.Storage.objectList, params);
      replacerResourceList = [
        ...replacerResourceList,
        ...(data.dataList as any[]).map<InformalNodeManagerPageModelState['replaceModal_Replacer_ResourceList'][number]>((ob) => {
          const objectName: string = ob.bucketName + '/' + ob.objectName;
          return {
            checked: false,
            id: ob.objectId,
            identity: 'object',
            name: objectName,
            type: ob.resourceType,
            latestVersion: '',
            updateTime: FUtil.Format.formatDateTime(ob.updateDate),
            status: '',
            versions: [],
            versionRange: '',
          };
        }),
      ];
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replacer_ResourceList: replacerResourceList,
      },
    });

  },
  * onReplacerListCheckedChange({ payload }: OnReplacerListCheckedChangeAction, { select, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replacer_ResourceList: informalNodeManagerPage.replaceModal_Replacer_ResourceList.map<InformalNodeManagerPageModelState['replaceModal_Replacer_ResourceList'][number]>((rr) => {
          if (rr.id !== payload.id) {
            return {
              ...rr,
              checked: false,
            };
          }
          return {
            ...rr,
            checked: true,
          };
        }),
      },
    });
  },
  * onReplacerListVersionRangeChange({ payload }: OnReplacerListVersionRangeChangeAction, {
    put,
    select,
  }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replacer_ResourceList: informalNodeManagerPage.replaceModal_Replacer_ResourceList.map<InformalNodeManagerPageModelState['replaceModal_Replacer_ResourceList'][number]>((rr) => {
          if (rr.id !== payload.id) {
            return rr;
          }
          return {
            ...rr,
            versionRange: payload.versionRange,
          };
        }),
      },
    });
  },
  * onReplacerListLoadMore({}: OnReplacerListLoadMoreAction, {}: EffectsCommandMap) {

  },
  * onReplacedMount({}: OnReplacedMountAction, {}: EffectsCommandMap) {

  },
  * onReplacedUnmount({}: OnReplacedUnmountAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {},
    });
  },
  * onReplacedKeywordChange({ payload }: OnReplacedKeywordChangeAction, { put, select, call }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const payloadValue: string = payload.value;

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replaced_Keywords: payloadValue,
      },
    });

    const params: Parameters<typeof FServiceAPI.InformalNode.dependencyTree>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      keywords: payloadValue,
      resourceType: informalNodeManagerPage.showPage === 'theme' ? 'theme' : undefined,
      omitResourceType: informalNodeManagerPage.showPage === 'theme' ? undefined : 'theme',
    };

    const { data } = yield call(FServiceAPI.InformalNode.dependencyTree, params);

    let replacedSelectDependency = data.find((d: any) => d.name === payloadValue);

    const replacedTargetVersions: InformalNodeManagerPageModelState['replaceModal_Replaced_TargetVersions'] = replacedSelectDependency
      ? [
        { value: '', text: '全部版本' },
        ...replacedSelectDependency.versions.map((v: any) => {
          return {
            value: v,
            text: v,
          };
        }),
      ]
      : [];
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replaced_DependencyTreeList: data?.map((d: any) => d.name) || [],
        replaceModal_Replaced_SelectDependency: replacedSelectDependency || null,
        replaceModal_Replaced_TargetVersions: replacedTargetVersions,
        replaceModal_Replaced_TargetSelectedVersion: replacedTargetVersions.length > 0 ? replacedTargetVersions[0] : null,
      },
    });

    if (!replacedSelectDependency) {
      return;
    }

    const params3: Parameters<typeof FServiceAPI.InformalNode.searchTestResourcesByDependency>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      dependentEntityId: replacedSelectDependency.id,
      resourceType: informalNodeManagerPage.showPage === 'theme' ? 'theme' : undefined,
      omitResourceType: informalNodeManagerPage.showPage === 'theme' ? undefined : 'theme',
    };
    const { data: data3 } = yield call(FServiceAPI.InformalNode.searchTestResourcesByDependency, params3);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replaced_TreeData: (data3 as any[]).map<InformalNodeManagerPageModelState['replaceModal_Replaced_TreeData'][number]>((d: any) => {
          return {
            key: `${FUtil.Tool.generateRandomCode()}:${d.testResourceName}`,
            id: d.testResourceId,
            title: `${d.entityName}(${d.testResourceName})`,
          };
        }),
        replaceModal_Replaced_CheckedKeys: [],
      },
    });
  },
  * onReplacedEntityVersionChange({ payload }: OnReplacedEntityVersionChangeAction, {
    select,
    call,
    put,
  }: EffectsCommandMap) {

    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    if (!informalNodeManagerPage.replaceModal_Replaced_SelectDependency) {
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replaced_TargetSelectedVersion: informalNodeManagerPage.replaceModal_Replaced_TargetVersions.find((rtv) => {
          return rtv.value === payload.value;
        }) || null,
      },
    });

    const params3: Parameters<typeof FServiceAPI.InformalNode.searchTestResourcesByDependency>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      dependentEntityId: informalNodeManagerPage.replaceModal_Replaced_SelectDependency.id,
      resourceType: informalNodeManagerPage.showPage === 'theme' ? 'theme' : undefined,
      omitResourceType: informalNodeManagerPage.showPage === 'theme' ? undefined : 'theme',
      dependentEntityVersionRange: payload.value || undefined,
    };
    const { data: data3 } = yield call(FServiceAPI.InformalNode.searchTestResourcesByDependency, params3);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replaced_TreeData: (data3 as any[]).map<InformalNodeManagerPageModelState['replaceModal_Replaced_TreeData'][number]>((d: any) => {
          return {
            key: `${FUtil.Tool.generateRandomCode()}:${d.testResourceName}`,
            id: d.testResourceId,
            title: `${d.entityName}(${d.testResourceName})`,
          };
        }),
        replaceModal_Replaced_CheckedKeys: [],
      },
    });
  },
  * onReplacedTreeLoadData({ payload }: OnReplacedTreeLoadDataAction, { select, put, call }: EffectsCommandMap) {

    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    if (payload.pos.split('-').length !== 2) {
      return;
    }
    const params: Parameters<typeof FServiceAPI.InformalNode.dependencyTreeFilter>[0] = {
      testResourceId: payload.id,
      dependentEntityId: informalNodeManagerPage.replaceModal_Replaced_SelectDependency?.id || '',
      dependentEntityVersionRange: informalNodeManagerPage.replaceModal_Replaced_TargetSelectedVersion?.value || undefined,
    };
    const { data } = yield call(FServiceAPI.InformalNode.dependencyTreeFilter, params);
    const result = updateTreeData({
      list: informalNodeManagerPage.replaceModal_Replaced_TreeData as InformalNodeManagerPageModelState['replaceModal_Replaced_TreeData'],
      key: payload.key,
      children: organizeData(data[0].dependencies, payload.key),
    });

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Replaced_TreeData: result,
      },
    });
  },
  * onReplaceModalCancel({}: OnReplaceModalCancelAction, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Visible: false,
      },
    });
  },
  * onReplaceModalConfirm({}: OnReplaceModalConfirmAction, { select, call, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const rules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = (informalNodeManagerPage.node_RuleInfo?.testRules || []).map((rr) => {
      return rr.ruleInfo;
    });

    let needHandleRules: Array<IRules['add'] | IRules['alter'] | IRules['activate_theme']> = JSON.parse(JSON.stringify(rules));

    const simplifiedResults: string[][] = simplifiedRelationship(informalNodeManagerPage.replaceModal_Replaced_CheckedKeys)
      .map<string[]>((r) => {
        return r.split(':')
          .filter((_, i) => {
            return i !== 0;
          });
      });

    const replaced: IActions['replace']['content']['replaced'] = {
      name: informalNodeManagerPage.replaceModal_Replaced_SelectDependency?.name || '',
      versionRange: informalNodeManagerPage.replaceModal_Replaced_TargetSelectedVersion?.value || 'latest',
      type: informalNodeManagerPage.replaceModal_Replaced_SelectDependency?.type || 'object',
    };

    const replacerData = informalNodeManagerPage.replaceModal_Replacer_ResourceList.find((rr) => {
      return rr.checked;
    });

    const replacer: IActions['replace']['content']['replacer'] = {
      name: replacerData?.name || '',
      versionRange: replacerData?.versionRange || 'latest',
      type: replacerData?.identity || 'object',
    };

    for (const simplifiedResult of simplifiedResults) {
      const [key, ...arr] = simplifiedResult;
      const scope: IActions['replace']['content']['scopes'][number] = arr.map((o: string) => {
        if (o?.startsWith('$')) {
          return {
            name: o.replace('$', ''),
            type: 'resource',
            versionRange: 'latest',
          };
        } else {
          return {
            name: o.replace('#', ''),
            type: 'object',
            versionRange: 'latest',
          };
        }
      });

      needHandleRules = mergeRules({
        oldRules: needHandleRules,
        exhibitName: key,
        action: {
          operation: 'replace',
          content: {
            replaced,
            replacer,
            scopes: scope.length === 0 ? [] : [scope],
          },
        },
      });
    }

    const changedRules: Array<IRules['add'] | IRules['alter'] | any> = simplifiedResults.map((s) => {
      return needHandleRules.find((nhr) => {
        return nhr.exhibitName === s[0] && nhr.operation !== 'activate_theme';
      });
    });

    const allChangedKey: string[] = simplifiedResults.map((s) => {
      return s[0];
    });
    changedRules.sort((a) => {
      return allChangedKey.includes(a.exhibitName) ? -1 : 0;
    });

    const params3: Parameters<typeof FServiceAPI.InformalNode.rulesPreExecution>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      testRuleText: decompile(changedRules),
    };

    const { data: data3 } = yield call(FServiceAPI.InformalNode.rulesPreExecution, params3);

    const matchErrors: string[] = data3.map((d: any) => {
      return d.matchErrors;
    }).flat();

    if (matchErrors.length > 0) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          replaceModal_Errors: matchErrors,
        },
      });
      return;
    }

    const text: string = decompile(needHandleRules);

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      testRuleText: text,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    if (informalNodeManagerPage.showPage === 'exhibit') {
      yield put<FetchExhibitListAction>({
        type: 'fetchExhibitList',
        payload: {
          isRematch: true,
          isRestart: true,
        },
      });
    } else {
      yield put<FetchThemeListAction>({
        type: 'fetchThemeList',
        payload: {
          isRematch: true,
          isRestart: true,
        },
      });
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        replaceModal_Visible: false,
      },
    });
  },
};
